"use client";

import { useAppContext } from "@/context/Context";
import { useSession } from "next-auth/react";
import Image from "next/image";
import Link from "next/link";
import logo from "../../public/images/logo/logo.webp";
import logoDark from "../../public/images/light/logo/logo-dark.webp";
import avatar from "../../public/images/team/user.webp";
import Nav from "./Nav";
import UserMenu from "./UserMenu";
import DarkSwitch from "./dark-switch";
import { useEffect, useState, useCallback } from "react";

const HeaderDashboard = ({ display }) => {
  const {
    rightBar,
    setRightBar,
    activeMobileMenu,
    setActiveMobileMenu,
    isLightTheme,
    toggleTheme,
    bitsRefreshTick,
  } = useAppContext();

  const { data: session, status, update } = useSession();
  const [, setShouldRender] = useState(false);

  // --- NUEVO: obtener plan y bits restantes ---
  const apiUrl = process.env.NEXT_PUBLIC_API_URL;
  const [remainingBits, setRemainingBits] = useState(undefined);
  const [loadingRemainingBits, setLoadingRemainingBits] = useState(false);
  const [remainingBitsError, setRemainingBitsError] = useState(false);

  // Home Chat
  const fetchRemainingBits = useCallback(async () => {
    if (!session?.token) return;
    try {
      setLoadingRemainingBits(true);
      setRemainingBitsError(false);

      const res = await fetch(`${apiUrl}/user/get-remaining-bits`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session.token}`,
        },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const data = await res.json();
      setRemainingBits(data.remaining_bits);
    } catch {
      setRemainingBitsError(true);
    } finally {
      setLoadingRemainingBits(false);
    }
  }, [apiUrl, session?.token]);

  useEffect(() => {
    if (session) fetchRemainingBits();
  }, [bitsRefreshTick, session, fetchRemainingBits]);

  const [, setLoadingTimezone] = useState(false);
  const [, setTimezoneError] = useState(false);

  const syncTimezone = useCallback(async () => {
    if (!session?.token) return;

    const browserTz =
      Intl?.DateTimeFormat?.().resolvedOptions?.().timeZone || "UTC";

    const storedTz = session?.user?.timezone || null;

    if (!browserTz || browserTz === storedTz) return;

    try {
      setLoadingTimezone(true);
      setTimezoneError(false);

      const res = await fetch(`${apiUrl}/user/update-timezone`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session.token}`,
        },
        body: JSON.stringify({ timezone: browserTz }),
      });

      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      await update({
        ...session,
        user: { ...session.user, timezone: browserTz },
      });
    } catch {
      setTimezoneError(true);
    } finally {
      setLoadingTimezone(false);
    }
  }, [apiUrl, session, update]);

  useEffect(() => {
    if (session) syncTimezone();
  }, [session, syncTimezone]);

  // Escucha logout en localStorage (sin cambios)
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === "logout") setShouldRender((p) => !p);
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  if (status === "loading") return null;

  // Texto que se mostrará
  const planText = (() => {
    if (loadingRemainingBits) return <>Cargando...</>;
    if (remainingBitsError) return <>—</>;
    if (typeof remainingBits === "undefined") return <>—</>;

    return (
      <>
        <i className="fa-solid fa-coins"></i>{" "}
        {remainingBits == null ? (
          <>bits ilimitados</>
        ) : (
          <>
            <strong>{remainingBits}</strong> bits restantes
          </>
        )}
      </>
    );
  })();

  return (
    <>
      {/* Conmutador de tema */}
      <DarkSwitch isLight={isLightTheme} switchTheme={toggleTheme} />

      <header className="rbt-dashboard-header rainbow-header header-default header-left-align rbt-fluid-header">
        <div className="container-fluid position-relative">
          <div className="row align-items-center justify-content-between">
            {/* Sección del logo */}
            <div className="col-lg-3 col-md-6 col-6">
              <div className="header-left d-flex">
                <div className="logo">
                  <Link href="/">
                    <Image
                      className="logo-light"
                      src={isLightTheme ? logo : logoDark}
                      width={135}
                      height={35}
                      alt="Corporate Logo"
                      priority
                    />
                  </Link>
                </div>
              </div>
            </div>

            {/* Menú principal */}
            <div className="col-lg-6 d-none d-lg-block text-center">
              <nav className="mainmenu-nav d-none d-lg-block text-center">
                <Nav />
              </nav>
            </div>

            {/* Usuario o login */}
            {session ? (
              <div className="col-lg-3 col-md-6 col-6">
                <div className="header-right">
                  {/* Menú móvil */}
                  <div className="mobile-menu-bar mr--10 ml--10 d-block d-lg-none">
                    <div className="hamberger">
                      <button
                        className="hamberger-button"
                        onClick={() => setActiveMobileMenu(!activeMobileMenu)}
                      >
                        <i className="feather-menu"></i>
                      </button>
                    </div>
                  </div>

                  {/* Panel de usuario */}
                  <div className="rbt-admin-panel account-access rbt-user-wrapper right-align-dropdown">
                    <div className="rbt-admin-card grid-style rbt-admin-bits-card">
                      <a className="d-flex align-items-center" href="#">
                        <div className="inner d-flex align-items-center">
                          <div className="img-box">
                            <Image src={avatar} alt="Admin" />
                          </div>
                          <div className="content text-center">
                            <p className="remaining-bits changelog_content pl--5 pr--5 pb--0 pt--0">
                              <span className="fixed mr--0">{planText}</span>
                            </p>
                            <p>{session.user.name}</p>
                          </div>
                        </div>
                        <div className="icon">
                          <i className="fa-sharp fa-solid fa-chevron-down"></i>
                        </div>
                      </a>
                    </div>
                    <div className="rbt-user-menu-list-wrapper">
                      <UserMenu />
                    </div>
                  </div>

                  {/* Botón barra lateral derecha */}
                  <div className={`expand-btn-grp ${display}`}>
                    <button
                      className={`bg-solid-primary popup-dashboardright-btn session-history-collapse-section ${
                        rightBar ? "" : "collapsed"
                      }`}
                      onClick={() => setRightBar(!rightBar)}
                    >
                      <i className="fa-solid fa-arrows-turn-to-dots"></i>
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="col-lg-3 col-md-6 col-6 position-static">
                <div className="header-right access-section">
                  <div className="header-btn">
                    <nav className="aiwave-tab">
                      <div className="tab-btn-grp nav nav-tabs d-flex flex-column flex-lg-row justify-content-center mb-3 mt-3 text-center">
                        <Link
                          href="/iniciar-sesion"
                          className="nav-link sign-in flex-fill mb-2 mb-lg-0"
                        >
                          Iniciar sesión
                        </Link>
                        <Link
                          href="/registrarse"
                          className="nav-link sign-up active flex-fill"
                        >
                          Regístrate gratis
                        </Link>
                      </div>
                    </nav>
                  </div>

                  <div className="mobile-menu-bar ml--5 d-flex justify-content-end d-lg-none">
                    <div className="hamberger">
                      <button
                        className="hamberger-button"
                        onClick={() => setActiveMobileMenu(!activeMobileMenu)}
                      >
                        <i className="fa-sharp fa-regular fa-bars"></i>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </header>
    </>
  );
};

export default HeaderDashboard;
